import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import {
  SUPPORT_REASON_MAX,
  type SupportOrderView,
} from "@shared/tvod-support";
import { PageContainer, PageHeader } from "../components/PageHeader";
import EditorCard from "../components/template/EditorCard";
import {
  EDITOR_MAX_WIDTH,
  fieldGrid,
} from "../components/template/editorLayout";
import { tokens } from "../theme";
import { describeError, useToast } from "../components/toast";
import { tvodStaffApi } from "../http/tvodStaff";
import { friendlyError } from "../utils/apiError";
import { PermissionGate } from "../auth/access";
import { money } from "../components/revenue/StatementView";
import { useFormat, type Formatters } from "../i18n/format";
import { translateKey } from "../i18n/translate";

/**
 * Order support (consumer TVOD §8, wave 2 slice F) - Nenda staff only.
 *
 * ## One order, by its ref
 *
 * A case starts with the customer quoting the ref on their receipt. This page
 * has no list of orders. Browsing customers is the separate, audited
 * *Portal customers* register (Sigge, 2026-10-04), whose order refs link
 * here as `?ref=<orderRef>` - looked up on arrival.
 *
 * ## Revoke and refund are different things
 *
 * *Revoke access* stops the titles now, for a recorded reason (fraud, a
 * chargeback, a mistaken grant). *Refund* asks Stripe to give the money back;
 * the access goes when Stripe confirms the refund, exactly as for a refund
 * made in Stripe's own dashboard. Both are audited under the staff user.
 */

const STATE_KEY = {
  pending: "orderSupportPage.state.pending",
  paid: "orderSupportPage.state.paid",
  failed: "orderSupportPage.state.failed",
  refunded: "orderSupportPage.state.refunded",
} as const satisfies Record<SupportOrderView["order"]["state"], string>;

const RENTAL_KEY: Record<string, string> = {
  not_started: "orderSupportPage.rental.not_started",
  active: "orderSupportPage.rental.active",
  expired: "orderSupportPage.rental.expired",
  revoked: "orderSupportPage.rental.revoked",
};

const REFUND_BLOCKED_KEY: Record<string, string> = {
  not_paid: "orderSupportPage.refundBlocked.not_paid",
  no_payment_ref: "orderSupportPage.refundBlocked.no_payment_ref",
  no_provider: "orderSupportPage.refundBlocked.no_provider",
};

const EMPTY = "—";

const whenAt = (fmt: Formatters, iso: string | null) =>
  iso ? fmt.dateTime(iso) : EMPTY;

type Action = "revoke" | "refund";

/** `?ref=` - the Portal customers card links an order here by its ref. */
function refFromUrl(): string {
  try {
    return new URLSearchParams(window.location.search).get("ref")?.trim() ?? "";
  } catch {
    return "";
  }
}

export default function OrderSupportPage() {
  const toast = useToast();
  const { t } = useTranslation(["offers", "common"]);
  const [ref, setRef] = useState(refFromUrl);
  const [view, setView] = useState<SupportOrderView | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState<Action | null>(null);

  const lookUp = async (e?: FormEvent) => {
    e?.preventDefault();
    const wanted = ref.trim();
    if (!wanted) return;
    setBusy(true);
    setError(null);
    setNotFound(false);
    try {
      setView(await tvodStaffApi.order(wanted));
    } catch (err) {
      setView(null);
      if ((err as { status?: number }).status === 404) setNotFound(true);
      else setError(friendlyError(t, err));
    } finally {
      setBusy(false);
    }
  };

  // Arriving with `?ref=` looks that order up once.
  useEffect(() => {
    if (refFromUrl()) void lookUp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("orderSupportPage.eyebrow")}
        title={t("orderSupportPage.title")}
        subtitle={t("orderSupportPage.subtitle")}
      />
      <Stack spacing={2.5} sx={{ maxWidth: EDITOR_MAX_WIDTH }}>
        <Box
          component="form"
          onSubmit={lookUp}
          sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
        >
          <TextField
            size="small"
            label={t("orderSupportPage.orderReference")}
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            sx={{ flex: "1 1 320px", maxWidth: 420 }}
            inputProps={{ "data-testid": "support-ref", spellCheck: false }}
            helperText={t("orderSupportPage.orderReferenceHelp")}
          />
          <Button
            type="submit"
            variant="contained"
            startIcon={<SearchIcon />}
            disabled={busy || !ref.trim()}
            data-testid="support-lookup"
          >
            {t("orderSupportPage.lookUp")}
          </Button>
        </Box>

        {busy && !view ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={26} />
          </Stack>
        ) : null}
        {notFound ? (
          <Alert severity="info" data-testid="support-not-found">
            {t("orderSupportPage.notFound")}
          </Alert>
        ) : null}
        {error ? <Alert severity="error">{error}</Alert> : null}

        {view ? (
          <OrderCards view={view} busy={busy} onAction={(a) => setAction(a)} />
        ) : null}
      </Stack>

      {view && action ? (
        <ReasonDialog
          key={action}
          action={action}
          orderRef={view.order.orderRef}
          onClose={() => setAction(null)}
          onDone={(next, message) => {
            if (next) setView(next);
            toast.success(message);
            setAction(null);
          }}
        />
      ) : null}
    </PageContainer>
  );
}

function OrderCards({
  view,
  busy,
  onAction,
}: {
  view: SupportOrderView;
  busy: boolean;
  onAction: (action: Action) => void;
}) {
  const { order, customer, actions } = view;
  const { t } = useTranslation(["offers", "common"]);
  const fmt = useFormat();
  const when = (iso: string | null) => whenAt(fmt, iso);
  return (
    <Stack spacing={2.5} data-testid="support-order">
      <EditorCard
        eyebrow={t("orderSupportPage.orderEyebrow", { ref: order.orderRef })}
        action={
          <Chip
            size="small"
            label={
              STATE_KEY[order.state] ? t(STATE_KEY[order.state]) : order.state
            }
            color={order.state === "paid" ? "primary" : "default"}
            variant={order.state === "paid" ? "filled" : "outlined"}
            data-testid="support-state"
          />
        }
      >
        <Box sx={fieldGrid(220)}>
          <Fact label={t("orderSupportPage.facts.soldOn")}>
            {order.portal.name}
          </Fact>
          <Fact label={t("orderSupportPage.facts.offer")}>
            {order.offerTitle || order.offerId}
          </Fact>
          <Fact label={t("orderSupportPage.facts.amount")}>
            {money(fmt, order.amountMinor / 100, order.currency)}
          </Fact>
          <Fact label={t("orderSupportPage.facts.paid")}>
            {when(order.paidAt)}
          </Fact>
          <Fact label={t("orderSupportPage.facts.refunded")}>
            {when(order.refundedAt)}
          </Fact>
          <Fact label={t("orderSupportPage.facts.stripePayment")}>
            {order.paymentRef ?? EMPTY}
          </Fact>
          <Fact label={t("orderSupportPage.facts.rentalTerms")}>
            {order.playWindowHours
              ? t("orderSupportPage.rentalTermsFrom", {
                  hours: order.playWindowHours,
                  start:
                    order.windowStarts === "on_first_play"
                      ? t("orderSupportPage.firstPlay")
                      : t("orderSupportPage.purchase"),
                })
              : EMPTY}
            {order.startWindowDays && order.windowStarts === "on_first_play"
              ? t("orderSupportPage.startWithin", {
                  days: order.startWindowDays,
                })
              : ""}
          </Fact>
          <Fact label={t("orderSupportPage.facts.customer")}>
            {customer.email}
            <Typography
              variant="caption"
              sx={{ display: "block", color: tokens.inkFaint }}
            >
              {t("orderSupportPage.account", {
                member: customer.memberId ?? EMPTY,
                status: customer.status,
              })}
            </Typography>
          </Fact>
        </Box>
      </EditorCard>

      <EditorCard
        eyebrow={t("orderSupportPage.titles.eyebrow")}
        hint={t("orderSupportPage.titles.hint")}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {t("orderSupportPage.titles.columns.title")}
              </TableCell>
              <TableCell>
                {t("orderSupportPage.titles.columns.state")}
              </TableCell>
              <TableCell>
                {t("orderSupportPage.titles.columns.started")}
              </TableCell>
              <TableCell>{t("orderSupportPage.titles.columns.ends")}</TableCell>
              <TableCell>
                {t("orderSupportPage.titles.columns.startBy")}
              </TableCell>
              <TableCell align="right">
                {t("orderSupportPage.titles.columns.plays")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {view.titles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t("orderSupportPage.titles.empty")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              view.titles.map((title) => (
                <TableRow
                  key={title.assetId}
                  data-testid={`support-title-${title.assetId}`}
                >
                  <TableCell
                    sx={{ fontFamily: "fontFamilyMono", typography: "meta" }}
                  >
                    {title.assetId}
                  </TableCell>
                  <TableCell>
                    {RENTAL_KEY[title.state]
                      ? translateKey(t, `offers:${RENTAL_KEY[title.state]}`)
                      : title.state}
                  </TableCell>
                  <TableCell>{when(title.startedAt)}</TableCell>
                  <TableCell>{when(title.endsAt)}</TableCell>
                  <TableCell>{when(title.startBy)}</TableCell>
                  <TableCell align="right">
                    {view.playback.byTitle[title.assetId] ?? 0}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <Typography
          variant="body2"
          sx={{ mt: 1.5, color: tokens.inkMuted }}
          data-testid="support-playback"
        >
          {view.playback.sessions === 0
            ? t("orderSupportPage.playback.never")
            : t("orderSupportPage.playback.sessions", {
                count: view.playback.sessions,
                last: when(view.playback.lastStartedAt),
              })}
          {view.playback.open
            ? t("orderSupportPage.playback.playingNow", {
                asset: view.playback.open.assetId,
              })
            : ""}
          {Object.keys(view.playback.endReasons).length
            ? t("orderSupportPage.playback.ended", {
                reasons: Object.entries(view.playback.endReasons)
                  .map(([k, n]) => `${k.replace(/_/g, " ")} ${n}`)
                  .join(", "),
              })
            : ""}
        </Typography>
      </EditorCard>

      <EditorCard
        eyebrow={t("orderSupportPage.lines.eyebrow")}
        hint={t("orderSupportPage.lines.hint")}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("orderSupportPage.lines.columns.kind")}</TableCell>
              <TableCell>{t("orderSupportPage.lines.columns.title")}</TableCell>
              <TableCell>
                {t("orderSupportPage.lines.columns.creditedTo")}
              </TableCell>
              <TableCell>{t("orderSupportPage.lines.columns.date")}</TableCell>
              <TableCell align="right">
                {t("orderSupportPage.lines.columns.amount")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {view.lines.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    {t("orderSupportPage.lines.empty")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              view.lines.map((l) => (
                <TableRow key={`${l.kind}-${l.assetId}`}>
                  <TableCell>
                    {l.kind === "sale"
                      ? t("orderSupportPage.lines.sale")
                      : t("orderSupportPage.lines.refund")}
                  </TableCell>
                  <TableCell
                    sx={{ fontFamily: "fontFamilyMono", typography: "meta" }}
                  >
                    {l.assetId}
                  </TableCell>
                  <TableCell>{l.titleOwnerId}</TableCell>
                  <TableCell>{when(l.at)}</TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: l.amountMinor < 0 ? tokens.error : undefined }}
                  >
                    {money(fmt, l.amountMinor / 100, l.currency)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </EditorCard>

      <EditorCard eyebrow={t("orderSupportPage.actions.eyebrow")}>
        <Stack spacing={1.5}>
          {view.grant?.revokedAt ? (
            <Alert severity="warning" data-testid="support-revoked">
              {t("orderSupportPage.actions.revoked", {
                when: when(view.grant.revokedAt),
                who: view.grant.revokedBy || EMPTY,
                reason: view.grant.revokedReason
                  ? `: ${view.grant.revokedReason}`
                  : "",
              })}
            </Alert>
          ) : null}
          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            <PermissionGate resource="storefront" level="edit" place="nenda">
              <Button
                variant="outlined"
                color="error"
                disabled={busy || !actions.revoke.allowed}
                onClick={() => onAction("revoke")}
                data-testid="support-revoke"
              >
                {t("orderSupportPage.actions.revokeAccess")}
              </Button>
            </PermissionGate>
            <PermissionGate
              resource="storefront"
              level="administrate"
              place="nenda"
            >
              <Button
                variant="outlined"
                disabled={busy || !actions.refund.allowed}
                onClick={() => onAction("refund")}
                data-testid="support-refund"
              >
                {t("orderSupportPage.actions.refundInStripe")}
              </Button>
            </PermissionGate>
          </Stack>
          {!actions.refund.allowed && actions.refund.reason ? (
            <Typography variant="caption" sx={{ color: tokens.inkMuted }}>
              {REFUND_BLOCKED_KEY[actions.refund.reason]
                ? translateKey(
                    t,
                    `offers:${REFUND_BLOCKED_KEY[actions.refund.reason]}`
                  )
                : actions.refund.reason}
            </Typography>
          ) : null}
        </Stack>
      </EditorCard>
    </Stack>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: tokens.inkFaint }}>
        {label}
      </Typography>
      <Typography variant="body2" component="div" sx={{ color: tokens.ink }}>
        {children}
      </Typography>
    </Box>
  );
}

function ReasonDialog({
  action,
  orderRef,
  onClose,
  onDone,
}: {
  action: Action;
  orderRef: string;
  onClose: () => void;
  onDone: (next: SupportOrderView | null, message: string) => void;
}) {
  const toast = useToast();
  const { t } = useTranslation(["offers", "common"]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (action === "revoke") {
        const res = await tvodStaffApi.revoke(orderRef, reason.trim());
        onDone(
          res.order,
          res.endedStreams > 0
            ? t("orderSupportPage.dialog.revokedAndStopped", { ref: orderRef })
            : t("orderSupportPage.dialog.revoked", { ref: orderRef })
        );
      } else {
        await tvodStaffApi.refund(orderRef, reason.trim());
        onDone(
          null,
          t("orderSupportPage.dialog.refundRequested", { ref: orderRef })
        );
      }
    } catch (err) {
      setError(friendlyError(t, err));
      toast.error(
        describeError(
          t,
          action === "revoke" ? "revokeOrderAccess" : "refundOrder",
          err,
          { order: orderRef }
        )
      );
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {action === "revoke"
          ? t("orderSupportPage.dialog.revokeTitle", { ref: orderRef })
          : t("orderSupportPage.dialog.refundTitle", { ref: orderRef })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
            {action === "revoke"
              ? t("orderSupportPage.dialog.revokeHelp")
              : t("orderSupportPage.dialog.refundHelp")}
          </Typography>
          <TextField
            label={t("orderSupportPage.dialog.reason")}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            required
            inputProps={{
              maxLength: SUPPORT_REASON_MAX,
              "data-testid": "support-reason",
            }}
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t("common:buttons.cancel")}
        </Button>
        <Button
          variant="contained"
          color={action === "revoke" ? "error" : "primary"}
          disabled={busy || !reason.trim()}
          onClick={() => void submit()}
          data-testid="support-confirm"
        >
          {action === "revoke"
            ? t("orderSupportPage.actions.revokeAccess")
            : t("orderSupportPage.dialog.requestRefund")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
